import { weightedRoll, type RandomSource } from "@yjsgames/weighted-roll";
import { FOOTPRINT_IDS, type FootprintId } from "./moduleFootprints.js";
import { SHAPE_CATALOG, FALLBACK_SHAPE_ID } from "./moduleShapes.js";

/** 0~1 난수를 반환하는 함수 타입 — 주입으로 테스트 결정론 확보. @yjsgames/weighted-roll RandomSource 동형. */
export type PieceRandomSource = RandomSource;

/**
 * 조각(piece) — 모듈이 그리드에서 갖는 한 덩어리 외형: 연결 모양(shapeId) + 점유 칸(footprintId).
 * 과거엔 shape(배치 시 풀 첫 항목 고정)와 footprint(획득 시 전역 랜덤)가 분리된 두 축이라 같은 모듈이
 * 1칸/2칸 들쭉날쭉했다. 이제 둘을 "조각" 하나로 묶어, 모듈은 정의된 조각 풀(modules.tsv `pieces`)에서만
 * 한 조각을 **획득 시 뽑아 인스턴스에 고정**한다 — 풀 구성으로 일관성/다양성을 데이터가 통제한다.
 */
export interface ModulePiece {
  /** SHAPE_CATALOG에 등록된 연결 모양 식별자 (미지 값은 parsePieces에서 FALLBACK_SHAPE_ID로 보정). */
  readonly shapeId: string;
  /** 그리드 내 점유 칸 형태 식별자 (미지/누락 시 parsePieces에서 "single"로 보정). */
  readonly footprintId: FootprintId;
  /**
   * 이 조각이 획득 추첨 풀에 등장하는 최소 클리어 챕터 (bestChapter 기준, 미지정 = 1 = 제한 없음).
   * modules.tsv `pieces` 토큰 3번째(`shapeId:footprintId:chapter`)로 조각별 지정 — 초반엔 1칸만,
   * 2칸 조각은 높은 챕터부터 같은 데이터로 통제한다(pickPiece가 maxChapter로 필터).
   */
  readonly unlockChapter?: number;
}

/** 알 수 없는/빈 풀의 안전 기본 조각 (가로직선 1칸 — Start 좌측 연결 보장). */
export const FALLBACK_PIECE: ModulePiece = { shapeId: FALLBACK_SHAPE_ID, footprintId: "single" };

const FOOTPRINT_SET = new Set<string>(FOOTPRINT_IDS);

/**
 * modules.tsv `pieces` 컬럼(`shapeId:footprintId[:chapter]|...`) → 조각 배열.
 * 미지 shapeId는 FALLBACK_SHAPE_ID로, 미지/누락 footprintId는 "single"로 보정한다. 빈 입력 = [FALLBACK_PIECE].
 * 3번째 토큰(chapter)이 양의 정수면 unlockChapter로 부여(조각별 클리어 챕터 해금), 누락/비정수/0이하면 미부여(=제한 1).
 */
export function parsePieces(raw: string): ModulePiece[] {
  const pieces = raw
    .split("|")
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((tok) => {
      const [shapeRaw, footRaw, chapterRaw] = tok.split(":");
      const shapeId = shapeRaw && SHAPE_CATALOG[shapeRaw] ? shapeRaw : FALLBACK_SHAPE_ID;
      const footprintId =
        footRaw && FOOTPRINT_SET.has(footRaw) ? (footRaw as FootprintId) : "single";
      const chapter = chapterRaw ? Number.parseInt(chapterRaw, 10) : NaN;
      const piece: ModulePiece =
        Number.isFinite(chapter) && chapter > 1
          ? { shapeId, footprintId, unlockChapter: chapter }
          : { shapeId, footprintId };
      return piece;
    });
  return pieces.length > 0 ? pieces : [FALLBACK_PIECE];
}

/** 조각 풀의 대표 조각 = 첫 항목 (빈 풀이면 FALLBACK). 인스턴스 없는 정의 단위 미리보기(도감 등)에 쓴다. */
export function defaultPieceOf(pieces: readonly ModulePiece[]): ModulePiece {
  return pieces[0] ?? FALLBACK_PIECE;
}

/**
 * 조각 풀을 (shapeId, footprintId) 쌍 기준으로 순서 보존 중복제거 (순수).
 * 도감 카드·정보 팝업의 "조각 풀" 표시 공용 — shapeId만으로 dedup하면 footprint가 소실돼
 * 모든 항목을 한 footprint로 그리게 되므로(2칸이 1칸으로/세로직선이 가로직선으로 변형) 쌍으로 보존한다.
 */
export function dedupePieces(pieces: readonly ModulePiece[]): ModulePiece[] {
  const seen = new Set<string>();
  const out: ModulePiece[] = [];
  for (const p of pieces) {
    const key = `${p.shapeId}:${p.footprintId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

/**
 * 조각 해금 여부 — 도달 챕터(maxChapter=bestChapter)가 조각의 unlockChapter(미지정=1) 이상이면 해금.
 * 추첨 필터(pickPiece)와 UI 잠금 표시(정보 팝업·도감)가 공유하는 단일 술어(SSoT).
 */
export function isPieceUnlocked(piece: ModulePiece, maxChapter: number): boolean {
  return (piece.unlockChapter ?? 1) <= maxChapter;
}

/**
 * 획득 시 조각 풀에서 1개를 균등 추첨 (순수). 풀 길이 1이면 rng 미소비(결정론 + 난수 스트림 보존),
 * 다항목이면 @yjsgames/weighted-roll로 균등 가중 1회 추첨. 빈 풀은 FALLBACK_PIECE.
 *
 * maxChapter(=bestChapter, 기본 Infinity) 미만 해금 조각은 풀에서 제외해 "초반엔 1칸만/멀티셀은 나중" 통제.
 * 모든 조각이 해금 미달이면(예: single 없는 burst류 + 저챕터) 원본 풀로 fallback해 빈 결과/undefined를 막는다.
 */
export function pickPiece(
  rng: PieceRandomSource,
  pieces: readonly ModulePiece[],
  maxChapter = Infinity,
): ModulePiece {
  if (pieces.length === 0) return FALLBACK_PIECE;
  const unlocked = pieces.filter((p) => isPieceUnlocked(p, maxChapter));
  const pool = unlocked.length > 0 ? unlocked : pieces; // 전부 미달이면 원본 fallback
  if (pool.length === 1) return pool[0]!;
  return weightedRoll(
    rng,
    pool.map((p) => ({ value: p, weight: 1 })),
    pool[0]!,
  );
}
